import { NextPageWithLayout } from '@/pages/_app';
import { GetStaticProps, GetStaticPropsContext, InferGetServerSidePropsType } from 'next';
import { getServerSideProps } from 'next/dist/build/templates/pages';
import React, { useState } from 'react';
import { CustomHead, Layout } from '@/components/Commons';
import { AIKA_LOGO_SQUARE } from '@/shared/constants';
import { productSans } from '@/constants/fonts';
import Image from 'next/image';

const BlogExpoDetailsPage: NextPageWithLayout<InferGetServerSidePropsType<typeof getServerSideProps>> = () => {
  const [lang, setLang] = useState<'en' | 'ru'>('en');
  return (
    <section className={`text-white ${productSans.className} mb-32`}>
      <div className="mx-auto mt-40 max-w-5xl text-xl max-md:mt-28 max-md:w-full max-md:px-4">
        <div className="prose lg:prose-xl dark:prose-invert lg:prose-h1:text-4xl mx-auto mb-10 break-words lg:mt-20">
          <div className="mb-6 text-4xl max-md:text-2xl">
            {lang === 'en' ? (
              <h1>AIKA at the World Expo 2025 Osaka: Where Innovation Meets Humanity</h1>
            ) : (
              <h1>AIKA на Всемирной выставке EXPO 2025 Osaka: где инновации встречаются с человечностью</h1>
            )}
          </div>
          <div className="mb-16 flex w-full items-center justify-between max-md:mb-10">
            <div className="flex flex-1 gap-2 text-sm italic">
              <span>Expo</span>
              <span>20 Jan 2026</span>
            </div>
            <div className="text-right">
              <span className="group flex w-[60px] cursor-pointer overflow-hidden rounded-md">
                <span
                  className={`w-[30px] items-center justify-center bg-gray-200 pt-1 text-center text-sm text-black ${lang === 'en' ? 'bg-white' : ''}`}
                  onClick={() => setLang('en')}
                >
                  🇺🇸
                </span>
                <span
                  className={`w-[30px] items-center justify-center bg-gray-200 pt-1 text-center text-sm text-black ${lang === 'ru' ? 'bg-white' : ''}`}
                  onClick={() => setLang('ru')}
                >
                  🇷🇺
                </span>
              </span>
            </div>
          </div>
          <div className="mb-4 aspect-video w-full overflow-hidden rounded-2xl">
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/iK_ncqFn5UA?si=c_NggCqwDOXkjr2b&amp;start=27"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            ></iframe>
          </div>
          <p className="mb-4 text-base font-extralight">
            {lang === 'en' ? (
              <span>
                This year, AIKA had the privilege of participating in one of the most significant global events of 2025
                — the World Expo in Osaka, Japan. Held on the artificial island of Yumeshima under the theme "Designing
                Future Society for Our Lives," EXPO 2025 brought together over 160 countries and welcomed more than 29
                million visitors from around the world.
              </span>
            ) : (
              <span>
                В этом году AIKA удостоилась чести участвовать в одном из самых значимых глобальных событий 2025 года —
                Всемирной выставке в Осаке, Япония. Проходившая на искусственном острове Юмэсима под девизом «Проектируя
                будущее общество для нашей жизни», EXPO 2025 объединила более 160 стран и приняла свыше 29 миллионов
                посетителей со всего мира.
              </span>
            )}
          </p>
          <h2 className="mb-4">
            {lang === 'en' ? 'A Platform for Global Dialogue' : 'Платформа для глобального диалога'}
          </h2>
          <p className="mb-4 text-base font-extralight">
            {lang === 'en' ? (
              <span>
                The Expo's focus on "Saving Lives," "Empowering Lives," and "Connecting Lives" aligned perfectly with
                AIKA's mission. As a platform dedicated to mental health and Intelligence Augmentation for Gen-Z, we
                found ourselves among innovators, thought leaders, and organizations united by a common goal: building
                technologies that genuinely serve humanity.{' '}
              </span>
            ) : (
              <span>
                Фокус выставки на темах «Сохранение жизни», «Расширение возможностей жизни» и «Соединение жизней»
                идеально соответствовал миссии AIKA. Как платформа, посвящённая ментальному здоровью и Intelligence
                Augmentation для поколения Z, мы оказались среди инноваторов, лидеров мнений и организаций, объединённых
                общей целью: создание технологий, которые по-настоящему служат человечеству.
              </span>
            )}
          </p>
          <div className="relative mb-4 aspect-video w-full overflow-hidden rounded-2xl">
            <Image
              style={{ objectFit: 'cover' }}
              src="/images/blogs/expo/78008fae31a0da75223fc74e3c2fb48f.jpg"
              fill
              alt="e"
            />
          </div>
          <p className="mb-4 text-base font-extralight">
            {lang === 'en' ? (
              <span>
                Presenting AIKA on this world stage was an honor. Our speech highlighted how AI can be a force for
                empowerment — not replacing human connection, but deepening our understanding of ourselves and others.
                The international audience, representing diverse cultures and perspectives, responded with enthusiasm
                and curiosity about our approach to mental wellness through technology.
              </span>
            ) : (
              <span>
                Представить AIKA на этой мировой сцене было честью. Наше выступление продемонстрировало, как ИИ может
                быть силой для расширения возможностей — не заменяя человеческую связь, а углубляя наше понимание себя и
                других. Международная аудитория, представляющая разнообразные культуры и перспективы, откликнулась с
                энтузиазмом и интересом к нашему подходу к ментальному благополучию через технологии.
              </span>
            )}
          </p>
          <div className="relative mb-4 aspect-video w-full overflow-hidden rounded-2xl">
            <Image
              style={{ objectFit: 'cover' }}
              src="/images/blogs/expo/3abc2d8ad944657883a7b8dc35dbf99d.jpg"
              fill
              alt="e"
            />
          </div>
          <div className="mb-4 flex gap-4 max-md:flex-col">
            <div className="relative aspect-[0.75] w-2/5 overflow-hidden rounded-2xl max-md:w-full">
              <Image
                style={{ objectFit: 'cover' }}
                src="/images/blogs/expo/1124795ec6cea6762a8f2ec8e6128378.jpg"
                fill
                alt="e"
              />
            </div>
            <div className="flex-1/2">
              <h2 className="mb-4">{lang === 'en' ? 'Discovering Osaka' : 'Открывая Осаку'}</h2>
              <p className="mb-4 text-base font-extralight">
                {lang === 'en' ? (
                  <span>
                    Beyond the Expo grounds, Osaka itself became part of our experience. The city seamlessly blends
                    centuries of tradition with cutting-edge innovation — a philosophy that resonates deeply with our
                    work at AIKA. Walking through its vibrant streets, experiencing its renowned hospitality, and
                    witnessing how technology integrates into daily life provided fresh inspiration for our team.
                  </span>
                ) : (
                  <span>
                    За пределами территории Expo сама Осака стала частью нашего опыта. Город органично сочетает
                    многовековые традиции с передовыми инновациями — философия, которая глубоко резонирует с нашей
                    работой в AIKA. Прогулки по оживлённым улицам, знакомство со знаменитым японским гостеприимством и
                    наблюдение за тем, как технологии интегрируются в повседневную жизнь, дали нашей команде свежее
                    вдохновение.
                  </span>
                )}
              </p>
              <h2 className="mb-4">{lang === 'en' ? 'Building Global Connections' : 'Создание глобальных связей'}</h2>
              <p className="mb-4 text-base font-extralight">
                {lang === 'en' ? (
                  <span>
                    One of the most valuable aspects of EXPO 2025 was the opportunity to connect with innovators,
                    entrepreneurs, and visionaries from across the globe. These conversations — sometimes spontaneous,
                    sometimes planned — opened new perspectives on how mental health technology is perceived and needed
                    in different parts of the world.
                  </span>
                ) : (
                  <span>
                    Одним из самых ценных аспектов EXPO 2025 стала возможность установить контакты с инноваторами,
                    предпринимателями и визионерами со всего мира. Эти разговоры — иногда спонтанные, иногда
                    запланированные — открыли новые перспективы на то, как технологии ментального здоровья
                    воспринимаются и востребованы в разных уголках мира.
                  </span>
                )}
              </p>
              <p className="mb-4 text-base font-extralight">
                {lang === 'en' ? (
                  <span>
                    We met founders building solutions for their own communities, researchers exploring the frontiers of
                    human-AI interaction, and investors seeking technologies that prioritize genuine impact over mere
                    disruption. Each conversation reinforced our belief that AIKA's mission transcends borders.
                  </span>
                ) : (
                  <span>
                    Мы встретили основателей, создающих решения для своих сообществ, исследователей, изучающих границы
                    взаимодействия человека и ИИ, и инвесторов, ищущих технологии, которые ставят во главу угла реальное
                    влияние, а не просто разрушение устоев. Каждый разговор укреплял нашу убеждённость в том, что миссия
                    AIKA выходит за пределы границ.
                  </span>
                )}
              </p>
            </div>
          </div>
          <h2 className="mb-4">{lang === 'en' ? 'Looking Forward' : 'Взгляд в будущее'}</h2>
          <p className="mb-4 text-base font-extralight">
            {lang === 'en' ? (
              <span>
                Returning from Osaka, we carry with us not only new connections and insights but a renewed sense of
                purpose. EXPO 2025 demonstrated that the world is ready for technology that puts humanity first —
                technology that saves, empowers, and connects lives.
              </span>
            ) : (
              <span>
                Возвращаясь из Осаки, мы несём с собой не только новые связи и инсайты, но и обновлённое чувство цели.
                EXPO 2025 продемонстрировала, что мир готов к технологиям, которые ставят человечность на первое место —
                технологиям, которые сохраняют, расширяют возможности и соединяют жизни.
              </span>
            )}
          </p>
          <p className="mb-4 text-base font-extralight">
            {lang === 'en' ? (
              <span>This is exactly what we are building at AIKA.</span>
            ) : (
              <span>Именно это мы и создаём в AIKA.</span>
            )}
          </p>
        </div>
      </div>
    </section>
  );
};

BlogExpoDetailsPage.getLayout = (page: React.ReactElement) => {
  return (
    <>
      <CustomHead
        title="AIKA at the World Expo 2025 Osaka: Where Innovation Meets Humanity"
        description='This year, AIKA had the privilege of participating in one of the most significant global events of 2025 — the World Expo in Osaka, Japan. Held on the artificial island of Yumeshima under the theme "Designing Future Society for Our Lives," EXPO 2025 brought together over 160 countries and welcomed more than 29 million visitors from around the world.'
        image={AIKA_LOGO_SQUARE}
      />
      <Layout hideHeader={false} stickyHeader={false}>
        {page}
      </Layout>
    </>
  );
};
export const getStaticProps = (async ({ locale }: GetStaticPropsContext) => {
  return {
    props: {
      messages: (await import(`@/locales/${locale}.json`)).default,
    },
  };
}) satisfies GetStaticProps;

export default BlogExpoDetailsPage;
